import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  memo,
} from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import invariant from "tiny-invariant";
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { reorder } from "@atlaskit/pragmatic-drag-and-drop/reorder";
import { getReorderDestinationIndex } from "@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index";
import {
  extractClosestEdge,
  attachClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";

import {
  reorderComponents,
  removeComponent,
  addComponent,
  selectCurrentTopMetrics,
  selectCurrentCards,
  selectAvailableTemplates,
  selectUsedComponents,
  ComponentItem,
} from "../../../../core/data/redux/dashboardSlice";
import { ChartConfigHelper } from "../helpers/chartConfigHelper";
import AddCardsModal from "./AddCardsModal";
import "../styles/EditDashboardPanel.scss";

interface EditDashboardPanelProps {
  isVisible: boolean;
  dashboardName?: string | any;
  isEmployee: boolean;
  userRoleChar: string | any;
  userDepartmentId: number | any;
}

type DragData = {
  type: "metric" | "card";
  itemId: string;
  instanceId: symbol;
};

interface DragState {
  draggedItem: string | null;
  hoveredItem: string | null;
  dragOverType: "metric" | "card" | null;
}

const DraggableItem = memo<{
  component: ComponentItem;
  type: "metric" | "card";
  isDragging: boolean;
  isHovered: boolean;
  instanceId: symbol;
  onRemove: (id: string, type: string) => void;
  onHover: (id: string | null) => void;
  onDragOver: (type: "metric" | "card", isOver: boolean) => void;
}>(
  ({
    component,
    type,
    isDragging,
    isHovered,
    instanceId,
    onRemove,
    onHover,
    onDragOver,
  }) => {
    const elementRef = useRef<HTMLDivElement>(null);

    const config = useMemo(
      () =>
        type === "metric"
          ? ChartConfigHelper.getMetricConfig(
              component.module,
              component.configKey
            )
          : ChartConfigHelper.getChartConfig(
              component.module,
              component.configKey
            ),
      [component.module, component.configKey, type]
    );

    useEffect(() => {
      const element = elementRef.current;
      invariant(element, "Element ref should be set");

      const dragData: DragData = { type, itemId: component.id, instanceId };

      return combine(
        draggable({ element, getInitialData: () => dragData }),
        dropTargetForElements({
          element,
          canDrop: ({ source }) => {
            const sourceData = source.data as DragData;
            return (
              sourceData.type === type &&
              sourceData.itemId !== component.id &&
              sourceData.instanceId === instanceId
            );
          },
          getData: ({ input }) =>
            attachClosestEdge(dragData, {
              element,
              input,
              allowedEdges: ["top", "bottom"],
            }),
          onDragEnter: () => {
            onHover(component.id);
            onDragOver(type, true);
          },
          onDragLeave: () => onHover(null),
        })
      );
    }, [component.id, type, instanceId, onHover, onDragOver]);

    const handleRemoveClick = useCallback(
      (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onRemove(component.id, type);
      },
      [component.id, type, onRemove]
    );

    const itemClassName = `component-item ${isDragging ? "dragging" : ""} ${
      isHovered ? "drop-target" : ""
    }`;

    return (
      <div
        ref={elementRef}
        className={itemClassName}
        data-testid={`draggable-item-${component.id}`}>
        <div className="component-info">
          <i className="ti ti-grid-dots me-2" />
          <span>{config?.title || component.title}</span>
        </div>
        <Link to="#" title="Remove Card" onClick={handleRemoveClick}>
          <i className="ti ti-circle-dashed-x fs-24 text-muted" />
        </Link>
      </div>
    );
  }
);

const DropContainer = memo<{
  children: React.ReactNode;
  type: "metric" | "card";
  className: string;
  isDraggingOver: boolean;
  instanceId: symbol;
  onDragOver: (type: "metric" | "card", isOver: boolean) => void;
}>(({ children, type, className, isDraggingOver, instanceId, onDragOver }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = containerRef.current;
    invariant(element, "Container ref should be set");

    return dropTargetForElements({
      element,
      canDrop: ({ source }) => {
        const sourceData = source.data as DragData;
        return sourceData.type === type && sourceData.instanceId === instanceId;
      },
      getData: () => ({ type: `${type}-container`, instanceId }),
      onDragEnter: () => onDragOver(type, true),
      onDragLeave: () => onDragOver(type, false),
    });
  }, [type, instanceId, onDragOver]);

  return (
    <div
      ref={containerRef}
      className={`${className} ${isDraggingOver ? "drag-over" : ""}`}
      data-container-type={type}>
      {children}
    </div>
  );
});

const EditDashboardPanel: React.FC<EditDashboardPanelProps> = ({
  isVisible,
  dashboardName,
  isEmployee,
  userRoleChar,
  userDepartmentId,
}) => {
  const dispatch = useDispatch();

  const {
    topMetricsComponents,
    cardsComponents,
    availableTemplates,
    usedComponents,
  } = useSelector((state: any) => ({
    topMetricsComponents:
      selectCurrentTopMetrics(state, userRoleChar, userDepartmentId) || [],
    cardsComponents:
      selectCurrentCards(state, userRoleChar, userDepartmentId) || [],
    availableTemplates:
      selectAvailableTemplates(state, userRoleChar, userDepartmentId) || [],
    usedComponents:
      selectUsedComponents(state, userRoleChar, userDepartmentId) || [],
  }));

  const [dragState, setDragState] = useState<DragState>({
    draggedItem: null,
    hoveredItem: null,
    dragOverType: null,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<"metrics" | "charts">("charts");
  const [instanceId] = useState(() => Symbol("dashboard-panel-instance"));

  const resetDragState = useCallback(() => {
    setDragState({ draggedItem: null, hoveredItem: null, dragOverType: null });
  }, []);

  const handleRemoveComponent = useCallback(
    (componentId: string, type: string) => {
      const section = type === "metric" ? "topMetrics" : "cards";
      dispatch(removeComponent({ section, id: componentId }));
    },
    [dispatch]
  );

  const handleComponentsReorder = useCallback(
    (reorderedComponents: ComponentItem[], section: "topMetrics" | "cards") => {
      dispatch(reorderComponents({ section, items: reorderedComponents }));
    },
    [dispatch]
  );

  const handleAddCards = useCallback(
    (selectedCards: any[]) => {
      selectedCards.forEach((card) => {
        const section =
          card.componentType === "metric" ? "topMetrics" : "cards";
        dispatch(
          addComponent({
            section,
            module: card.module,
            configKey: card.configKey,
            componentType: card.componentType,
            id: card.id,
          })
        );
      });
    },
    [dispatch]
  );

  const handleHover = useCallback((itemId: string | null) => {
    setDragState((prev) => ({ ...prev, hoveredItem: itemId }));
  }, []);

  const handleDragOver = useCallback(
    (type: "metric" | "card", isOver: boolean) => {
      setDragState((prev) => ({ ...prev, dragOverType: isOver ? type : null }));
    },
    []
  );

  const openModal = useCallback((type: "metrics" | "charts") => {
    setModalType(type);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => setIsModalOpen(false), []);

  useEffect(() => {
    return combine(
      monitorForElements({
        canMonitor({ source }) {
          return source.data.instanceId === instanceId;
        },
        onDragStart({ source }) {
          setDragState((prev) => ({
            ...prev,
            draggedItem: (source.data as DragData).itemId,
          }));
        },
        onDrop({ source, location }) {
          const data = source.data as DragData;
          resetDragState();

          if (!location.current.dropTargets.length) return;

          const target = location.current.dropTargets[0];
          const targetData = target.data as DragData;

          if (data.type !== targetData.type) return;

          const sourceList =
            data.type === "metric" ? topMetricsComponents : cardsComponents;
          const sourceIndex = sourceList.findIndex(
            (item) => item.id === data.itemId
          );
          const targetIndex = sourceList.findIndex(
            (item) => item.id === targetData.itemId
          );

          if (sourceIndex === -1 || targetIndex === -1) return;

          const closestEdge = extractClosestEdge(target.data);
          const destinationIndex = getReorderDestinationIndex({
            startIndex: sourceIndex,
            indexOfTarget: targetIndex,
            closestEdgeOfTarget: closestEdge,
            axis: "vertical",
          });

          if (destinationIndex !== sourceIndex) {
            const reorderedItems = reorder({
              list: sourceList,
              startIndex: sourceIndex,
              finishIndex: destinationIndex,
            });
            const section = data.type === "metric" ? "topMetrics" : "cards";
            handleComponentsReorder(reorderedItems, section);
          }
        },
      })
    );
  }, [
    instanceId,
    topMetricsComponents,
    cardsComponents,
    handleComponentsReorder,
    resetDragState,
  ]);

  const sectionConfigs = useMemo(
    () => [
      {
        title: "Top Metrics",
        components: topMetricsComponents,
        type: "metric" as const,
        onAddClick: () => openModal("metrics"),
        emptyIcon: "ti-chart-dots",
        emptyText: "No metrics added yet",
        addText: "Add More Metrics",
      },
      {
        title: "Cards",
        components: cardsComponents,
        type: "card" as const,
        onAddClick: () => openModal("charts"),
        emptyIcon: "ti-layout-cards",
        emptyText: "No cards added yet",
        addText: "Add More Cards",
      },
    ],
    [topMetricsComponents, cardsComponents, openModal]
  );

  const renderSection = useCallback(
    (config: (typeof sectionConfigs)[0]) => (
      <div key={config.type} className="customization-section">
        <h6 className="section-title">
          {config.title} ({config.components.length})
        </h6>
        {config.components.length > 0 ? (
          <DropContainer
            type={config.type}
            className="components-list"
            isDraggingOver={dragState.dragOverType === config.type}
            instanceId={instanceId}
            onDragOver={handleDragOver}>
            {config.components.map((component) => (
              <DraggableItem
                key={component.id}
                component={component}
                type={config.type}
                isDragging={dragState.draggedItem === component.id}
                isHovered={dragState.hoveredItem === component.id}
                instanceId={instanceId}
                onRemove={handleRemoveComponent}
                onHover={handleHover}
                onDragOver={handleDragOver}
              />
            ))}
          </DropContainer>
        ) : (
          <div className="empty-section text-muted p-3">
            <i className={`ti ${config.emptyIcon} me-2`} />
            <span>{config.emptyText}</span>
          </div>
        )}
        <Link to="#" title={config.addText} onClick={config.onAddClick}>
          <div className="add-more-card text-primary">
            <i className="ti ti-plus me-2" />
            <span>{config.addText}</span>
          </div>
        </Link>
      </div>
    ),
    [dragState, instanceId, handleRemoveComponent, handleHover, handleDragOver]
  );

  const panelClassName = `edit-dashboard-panel ${
    isVisible ? "panel-expanded" : "panel-collapsed"
  }`;

  return (
    <>
      <div className={panelClassName}>
        <div className="panel-header">
          <h5 className="panel-title">
            <i className="ti ti-settings me-2" />
            {dashboardName || "Customize Dashboard"}
          </h5>
          <></>
        </div>

        <div className="panel-content">{sectionConfigs.map(renderSection)}</div>
      </div>

      <AddCardsModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onAddCards={handleAddCards}
        availableTemplates={availableTemplates}
        usedComponents={usedComponents}
        modalType={modalType}
      />
    </>
  );
};

export default EditDashboardPanel;
